import { Keyboard, Search, X } from "lucide-react";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./keyboard-shortcuts.css";

export type KeyboardShortcut = {
  id: string;
  label: string;
  keys: readonly string[];
  scope: string;
  description?: string;
};

type Props = {
  /** Register only shortcuts wired up by the host; this dialog adds no global keyboard handlers. */
  shortcuts: readonly KeyboardShortcut[];
  onClose: () => void;
};

export function KeyboardShortcutsDialog({ shortcuts, onClose }: Props) {
  const id = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const visible = shortcuts.filter((shortcut) => terms.every((term) => normalize(`${shortcut.label} ${shortcut.keys.join(" ")} ${shortcut.scope} ${shortcut.description ?? ""}`).includes(term)));
  const scopes = [...new Set(visible.map((shortcut) => shortcut.scope))];

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    if (!element) return;
    if (typeof element.showModal === "function") element.showModal();
    else element.setAttribute("open", "");
    search.current?.focus();
    return () => {
      if (element.open && typeof element.close === "function") element.close();
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <dialog ref={dialog} className="keyboard-shortcuts-dialog" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} aria-modal="true"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onKeyDown={(event) => { event.stopPropagation(); if (event.key === "Escape") { event.preventDefault(); onClose(); } }}>
      <header className="keyboard-shortcuts-header"><div><Keyboard size={20} aria-hidden="true" /><h2 id={`${id}-title`}>Atalhos de teclado</h2></div><button type="button" aria-label="Fechar atalhos de teclado" onClick={onClose}><X size={19} /></button></header>
      <p id={`${id}-description`} className="keyboard-shortcuts-description">Os atalhos dependem da área em foco. Dentro de uma sessão remota, o teclado pode ser controlado pelo aplicativo conectado.</p>
      <label className="keyboard-shortcuts-search"><Search size={17} aria-hidden="true" /><input ref={search} type="search" aria-label="Buscar atalho" placeholder="Buscar ação, tecla ou aplicativo" value={query} onChange={(event) => setQuery(event.target.value)} autoComplete="off" /></label>
      <div className="keyboard-shortcuts-content">
        {scopes.map((scope, scopeIndex) => <section className="keyboard-shortcuts-group" key={scope} aria-labelledby={`${id}-scope-${scopeIndex}`}>
          <h3 id={`${id}-scope-${scopeIndex}`}>{scope}</h3>
          <dl>{visible.filter((shortcut) => shortcut.scope === scope).map((shortcut) => <div className="keyboard-shortcuts-row" key={shortcut.id}>
            <dt>{shortcut.label}{shortcut.description && <small>{shortcut.description}</small>}</dt>
            <dd>{shortcut.keys.map((key, index) => <Fragment key={`${key}-${index}`}>{index > 0 && <span aria-hidden="true">+</span>}<kbd>{key}</kbd></Fragment>)}</dd>
          </div>)}</dl>
        </section>)}
        {visible.length === 0 && <div className="keyboard-shortcuts-empty" role="status"><Keyboard size={28} aria-hidden="true" /><strong>Nenhum atalho encontrado</strong><span>Tente outra ação, tecla ou aplicativo.</span></div>}
      </div>
      <footer className="keyboard-shortcuts-footer">Atalhos do navegador e do sistema operacional podem ter prioridade.</footer>
    </dialog>, document.body,
  );
}

function normalize(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").trim();
}
